import {
    OnboardingData,
    StudyPlan,
    PlanTemplate,
    Task,
    TodayMission,
    MissionState
} from '../types/onboarding';
import { WEEKDAY_MAPPING, OSP_LABELS, ZSV_LABELS } from './onboardingPlan.constants';
import { getNextChapter } from './chapterDatabase';
import { getNextOspTask } from './ospTaskDatabase';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _OSP_LABELS_UNUSED = OSP_LABELS; // suppress warning; used for future labelling

// Content access mappings for Free vs Pro users
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FREE_ZSV_CHAPTERS: Record<string, string> = {
    'filosofie': 'filosoficke-smery',
    'sociologie': 'vznik-a-vyvoj-sociologie',
    'politologie': 'f4d0e5f6-b7c8-4901-d2e3-f4a5b6c7d8e9',
    'unsure': 'filosoficke-smery'
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PRO_ZSV_CHAPTERS: Record<string, string> = {
    'filosofie': 'filosoficke-smery',
    'filosofie-alt': 'filosofie-vedy',
    'sociologie': 'vznik-a-vyvoj-sociologie',
    'politologie': 'f4d0e5f6-b7c8-4901-d2e3-f4a5b6c7d8e9',
    'ekonomie': 'zaklady-makro-mikro-ekonomie',
    'unsure': 'filosoficke-smery'
};

const FREE_FLASHCARD_SETS: Record<string, string> = {
    'filosofie': 'filosofie',
    'psychologie': 'psychologie',
    'sociologie': 'sociologie',
    'unsure': 'filosofie'
};

const PRO_FLASHCARD_SETS: Record<string, string> = {
    'filosofie': 'filosofie',
    'sociologie': 'sociologie',
    'politologie': 'politologie',
    'ekonomie': 'ekonomie',
    'psychologie': 'psychologie',
    'unsure': 'filosofie'
};

export interface PlanGenerationResult {
    plan: StudyPlan;
    requiresUpgrade?: {
        topic: string;
        contentType: 'chapter' | 'flashcards';
        fallbackTopic: string;
    };
}

// Helper: Calculate days until exam
export function getDaysUntilExam(examDateStr: string | undefined): number | null {
    if (!examDateStr) return null;
    const examDate = new Date(examDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    examDate.setHours(0, 0, 0, 0);
    const diffTime = examDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
}

// Helper: Get rotation topic for free tier (cycles through available free topics)
const FREE_TOPICS = ['filosofie', 'sociologie', 'politologie', 'psychologie'];
function getRotationTopic(rotationIndex: number, isPro: boolean): string {
    if (isPro) {
        // Pro users get all topics including sociologie, ekonomie
        const allTopics = ['filosofie', 'sociologie', 'politologie', 'ekonomie', 'psychologie'];
        return allTopics[rotationIndex % allTopics.length];
    }
    // Free users only get: filosofie, psychologie (flashcards only), politologie
    return FREE_TOPICS[rotationIndex % FREE_TOPICS.length];
}

// Helper: Get topic label
function getTopicLabel(topic: string): string {
    return ZSV_LABELS[topic] || topic;
}

export const generatePlan = (data: OnboardingData, isPro: boolean = false, existingMissionState?: MissionState): PlanGenerationResult => {
    // 1. Determine Template
    let template: PlanTemplate = 'planner';
    if (data.timeframe === '1-2w') {
        template = 'sprinter';
    } else if (data.timeframe === 'unsure' || data.track === 'UNSURE') {
        template = 'explorer';
    }

    // 2. Schedule
    const weekDays = WEEKDAY_MAPPING[data.daysPerWeek] || WEEKDAY_MAPPING[5];

    // 3. Module Split (for BOTH)
    let moduleSplit = undefined;
    if (data.track === 'BOTH') {
        if (data.bothPriority === 'osp') {
            moduleSplit = { ospPercent: 60, zsvPercent: 40 };
        } else if (data.bothPriority === 'zsv') {
            moduleSplit = { ospPercent: 40, zsvPercent: 60 };
        } else {
            moduleSplit = { ospPercent: 50, zsvPercent: 50 };
        }
    }

    // 4. Focus Split - OSP
    let ospFocus = undefined;
    if (data.track === 'OSP' || data.track === 'BOTH') {
        let logic = 34, reading = 33, dataVal = 33;

        if (data.ospWeakness === 'logic') {
            logic = 50; reading = 25; dataVal = 25;
        } else if (data.ospWeakness === 'reading') {
            reading = 50; logic = 25; dataVal = 25;
        } else if (data.ospWeakness === 'data') {
            dataVal = 50; logic = 25; reading = 25;
        }

        ospFocus = { logic, reading, data: dataVal };
    }

    // 5. Focus Split - ZSV
    let zsvFocus = undefined;
    if (data.track === 'ZSV' || data.track === 'BOTH') {
        let filosofie = 25, sociologie = 25, politologie = 25, ekonomie = 25;

        if (data.zsvWeakness && data.zsvWeakness !== 'unsure') {
            const w = data.zsvWeakness;
            filosofie = w === 'filosofie' ? 50 : 16.66;
            sociologie = w === 'sociologie' ? 50 : 16.66;
            politologie = w === 'politologie' ? 50 : 16.66;
            ekonomie = w === 'ekonomie' ? 50 : 16.66;

            // Normalize to sum to 100
            const sum = filosofie + sociologie + politologie + ekonomie;
            const factor = 100 / sum;
            filosofie = Math.round(filosofie * factor);
            sociologie = Math.round(sociologie * factor);
            politologie = Math.round(politologie * factor);
            ekonomie = 100 - (filosofie + sociologie + politologie);
        }

        zsvFocus = { filosofie, sociologie, politologie, ekonomie };
    }

    // 6. Milestones
    let mockEveryDays = 8;
    if (template === 'sprinter') mockEveryDays = 6;
    if (template === 'explorer') mockEveryDays = 7;

    // Calculate current day index (default to 1 for new plans, increment for existing)
    const dayIndex = existingMissionState?.dayIndex || 1;
    const currentRotation = existingMissionState?.currentTopicRotation || 0;
    const completedChapters = existingMissionState?.completedChapters || [];

    const tasks: Task[] = [];
    let requiresUpgrade: PlanGenerationResult['requiresUpgrade'] = undefined;
    const totalMinutes = data.minutesPerDay;

    // Helper to generate OSP Task
    const generateOspTask = (indexOffset: number = 0, useWeakness: boolean = true) => {
        // If secondary task (offset > 0), don't use weakness to ensure variety
        const taskWeakness = useWeakness ? data.ospWeakness : undefined;
        // Use a large offset for secondary tasks to avoid overlap with primary rotation
        const effectiveIndex = dayIndex + (indexOffset * 10);
        const ospTask = getNextOspTask(effectiveIndex, taskWeakness);

        return {
            id: `osp-practice-${dayIndex}-${indexOffset}`,
            label: `Trénink: ${ospTask.title}`,
            minutes: 15,
            module: 'OSP' as 'OSP',
            deepLink: `/osp?topic=${ospTask.id}&prompt=${encodeURIComponent(ospTask.prompt)}`,
            kind: 'practice' as 'practice'
        };
    };

    // Helper to generate ZSV Tasks
    const generateZsvTasks = (count: number) => {
        const zsvTasks: Task[] = [];
        let focus: string;

        // Determine topic
        if (dayIndex <= 3 && data.zsvWeakness && data.zsvWeakness !== 'unsure') {
            focus = data.zsvWeakness;
        } else {
            focus = getRotationTopic(currentRotation, isPro);
        }

        const nextChapter = getNextChapter(focus, completedChapters, isPro);
        const chapter = nextChapter || getNextChapter('filosofie', completedChapters, isPro);

        if (!chapter) return []; // Should not happen

        const chapterId = chapter.id;
        const chapterTitle = chapter.title;

        if (!chapter.isFree && !isPro) {
            requiresUpgrade = {
                topic: focus,
                contentType: 'chapter',
                fallbackTopic: 'filosofie'
            };
        }

        // Task 1: Chapter Study (Always included if ZSV is present)
        if (count >= 1) {
            zsvTasks.push({
                id: `study-${dayIndex}`,
                label: `Studium: ${chapterTitle}`,
                minutes: 15,
                module: 'ZSV',
                deepLink: `/textbook?chapter=${chapterId}`,
                kind: 'chapter'
            });
        }

        // Task 2: Flashcards (Included if count >= 2)
        if (count >= 2) {
            const flashcardMap = isPro ? PRO_FLASHCARD_SETS : FREE_FLASHCARD_SETS;
            const setId = flashcardMap[focus] || flashcardMap['filosofie'];
            zsvTasks.push({
                id: `flash-${dayIndex}`,
                label: `Flashkarty: ${getTopicLabel(focus)}`,
                minutes: totalMinutes > 30 ? 15 : 10,
                module: 'ZSV',
                deepLink: `/flashcards?set=${setId}`,
                kind: 'flashcards'
            });
        }

        // Task 3: Recap Quiz (Included if count >= 3)
        // SPACED REPETITION: 50% chance to test a past chapter if available
        if (count >= 3) {
            let quizChapterId = chapterId;
            let quizLabel = 'Rychlé opakování: Kvíz';

            // If we have history of completed chapters, maybe pick one at random
            if (completedChapters.length > 0 && Math.random() > 0.5) {
                const randomPastChapterId = completedChapters[Math.floor(Math.random() * completedChapters.length)];
                // Verify it's not the current one (though spaced rep on current is also fine, but variety is better)
                if (randomPastChapterId !== chapterId) {
                    quizChapterId = randomPastChapterId;
                    quizLabel = 'Opakování: Z předchozích kapitol';
                    // We might want to look up the chapter title nicely, but for now generic label or look it up if we had a map
                }
            }

            zsvTasks.push({
                id: `recap-${dayIndex}`,
                label: quizLabel,
                minutes: 10,
                module: 'ZSV',
                deepLink: `/quiz-recap?chapter=${quizChapterId}`,
                kind: 'recap'
            });
        }

        return zsvTasks;
    };

    // --- Mission Logic Selection ---

    if (data.track === 'OSP') {
        // Pure OSP: 1 Task (Focused)
        tasks.push(generateOspTask(0, true));
    } else if (data.track === 'ZSV') {
        // Pure ZSV: 3 Tasks (Study, Flashcards, Recap)
        tasks.push(...generateZsvTasks(3));
    } else {
        // BOTH (Obojí)
        if (data.bothPriority === 'osp') {
            // Priority OSP: Always 2 OSP + 1 ZSV
            tasks.push(generateOspTask(0, true));
            tasks.push(generateOspTask(1, false));
            tasks.push(...generateZsvTasks(1));
        } else if (data.bothPriority === 'zsv') {
            // Priority ZSV: Always 2 ZSV + 1 OSP
            tasks.push(...generateZsvTasks(2));
            tasks.push(generateOspTask(0, true));
        } else {
            // Balanced: Alternate based on Day Index to ensure 50/50 split
            // Odd Days (1, 3, 5...): Focus ZSV (2 ZSV + 1 OSP)
            // Even Days (2, 4, 6...): Focus OSP (2 OSP + 1 ZSV)
            if (dayIndex % 2 !== 0) {
                // Odd Day -> ZSV Focus
                tasks.push(...generateZsvTasks(2));
                tasks.push(generateOspTask(0, true));
            } else {
                // Even Day -> OSP Focus
                tasks.push(generateOspTask(0, true));
                tasks.push(generateOspTask(1, false));
                tasks.push(...generateZsvTasks(1));
            }
        }
    }

    // Guard: Final check for sum
    const currentSum = tasks.reduce((sum, t) => sum + t.minutes, 0);
    if (currentSum !== totalMinutes && tasks.length > 0) {
        // Adjusting the last task to match total minutes
        tasks[tasks.length - 1].minutes += (totalMinutes - currentSum);
    }

    // Determine dominant module for the day (for compatibility with TodayMission type)
    let todayModule: 'OSP' | 'ZSV';
    if (data.track === 'ZSV') {
        todayModule = 'ZSV';
    } else if (data.track === 'OSP') {
        todayModule = 'OSP';
    } else {
        if (data.bothPriority === 'osp') {
            todayModule = 'OSP';
        } else if (data.bothPriority === 'zsv') {
            todayModule = 'ZSV';
        } else {
            // Balanced: Follows the daily focus
            todayModule = (dayIndex % 2 !== 0) ? 'ZSV' : 'OSP';
        }
    }

    const todayMission: TodayMission = {
        dayIndex,
        label: `Den ${dayIndex}`,
        estimatedMinutes: totalMinutes,
        tasks,
        todayModule
    };

    // Create mission state to track progress
    const missionState: MissionState = {
        dayIndex,
        currentTopicRotation: (currentRotation + 1) % (isPro ? 5 : 3), // Rotate to next topic for next day
        lastGeneratedAt: new Date().toISOString(),
        completedTaskIds: [], // Reset completed tasks for new day
        completedChapters: todayModule === 'ZSV' && tasks.some(t => t.kind === 'chapter')
            ? [...completedChapters, tasks.find(t => t.kind === 'chapter')!.deepLink.split('chapter=')[1]]
            : completedChapters, // Track seen chapters
    };

    const plan: StudyPlan = {
        template,
        cadence: {
            daysPerWeek: data.daysPerWeek,
            minutesPerDay: data.minutesPerDay
        },
        moduleSplit,
        focusSplit: {
            osp: ospFocus,
            zsv: zsvFocus
        },
        schedule: {
            weekDays
        },
        milestones: {
            diagnostic: true,
            mockEveryDays
        },
        todayMission,
        missionState
    };

    return {
        plan,
        requiresUpgrade
    };
};
